import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';

import { BehaviorSubject, catchError, firstValueFrom, Observable, of, switchMap, throwError } from 'rxjs';
import { BaseRestApiService } from 'src/app/service/baseRestApiService.service';
import { EndPoints, UrlProviderService } from 'src/app/service/urlprovider.service';
import { HTMLCompressionService } from './htmlcompression.service';
import { ExportParams } from 'src/app/model/exportparams';
import { environment } from 'src/environments/environment';
import { UtilityService } from './utility.service';
import { ApiFetchExportDataService } from './api-fetchexportdata.service';
import { Region } from '../model/region.model';
import { PrintViewerService } from '../component/coe/service/print-viewer-service.service';

@Injectable({
    providedIn: 'root'
})
export class ApiExportsService extends BaseRestApiService {
    htmlCompressionService: HTMLCompressionService;
    private timeoutResetSubject = new BehaviorSubject<void>(undefined);
    utilityService: UtilityService;
    apiFetchExportDataService: ApiFetchExportDataService;
    printViewerService: any;

    headers: HttpHeaders = new HttpHeaders()
        .set('Content-Type', 'application/json; charset=utf-8')
        .set('responseType', 'blob')
        .set('observe', 'response')
        .set('Accept', '*/*');
    httpOptions = {
        responseType: 'arraybuffer' as 'json',
        headers: this.headers
    };

    constructor(
        http: HttpClient,
        urlProvider: UrlProviderService,
        htmlCompressionService: HTMLCompressionService,
        utilityService: UtilityService,
        printViewerServiceObj: PrintViewerService
    ) {
        super(http, urlProvider);
        this.htmlCompressionService = htmlCompressionService;
        this.utilityService = utilityService;
        this.printViewerService = printViewerServiceObj;
    }

    exportPfsReportExcel(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlPFSExportData(), payload, this.httpOptions);
    }

    exportCoeSearchExcel(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlCoeExportData(), payload, this.httpOptions);
    }

    exportCoeReviewLogExcel(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlCoeReviewLogExportData(), payload, this.httpOptions);
    }

    exportEnrollmentReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlEnrollmentsExportData(), payload, this.httpOptions);
    }

    exportInaIlpReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlInaExportData(), payload, this.httpOptions);
    }

    exportGradeAgeAlignmentTableReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrl4DtMonGradeAgeAlignment('table'), payload, this.httpOptions);
    }

    exportGradeAgeAlignmentStudentsReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrl4DtMonGradeAgeAlignment('students'), payload, this.httpOptions);
    }

    exportCommunicationEventsReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlCommunicationEventExportData(), payload, this.httpOptions);
    }

    exportCountsRegionReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlCountsRegionReport(), payload, this.httpOptions);
    }

    exportCountsSchoolDistrictReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlCountsSchoolDistrictReport(), payload, this.httpOptions);
    }

    exportCountsStudentsReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlCountsStudentsReport(), payload, this.httpOptions);
    }

    exportDataMonitoringReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlDataMonitoringReport(), payload, this.httpOptions);
    }

    exportServiceReportExcel(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlServiceReportsExportData(), payload, this.httpOptions);
    }
  
    exportSdpStudentsReport(payload: any): Observable<any> {
        return this.http.post(this.urlProvider.getUrlSdpStudentsExport(), payload, this.httpOptions);
    }

    public getContactMechanismPurposeTypes(data: any): Observable<any> {
        return this.post<any>(
            EndPoints.parties_getContactMechanismPurposeTypes,
            false,
            '',
            data,
            (Headers = {
                ...this.urlProvider.baseGetOptions()
            })
        );
    }

    public async getSSDPReportXLSViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';
        let params: ExportParams;
        params = {
            strRegionCategory: data['strRegionCategory'],
            strDistrictId: data['strDistrictId'],
            strYear: data['strYear'],
            strExportType: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            strHtmlContent: 'test',
            strTemplateName: data['strTemplateName'],
            authObj1: authObject
        };
        const url = this.urlProvider.getUrlNoQueryParamViaPost(EndPoints.apiMsinExportsPDF);
        console.log('running post with url:');
        console.log(url);
        console.log('and params:');
        console.log(params);
        //const headers = new HttpHeaders();
        //headers.append('Access-Control-Allow-Origin',environment.headerHost);
        //headers.set('Access-Control-Request-Method','POST');

        var headers = {
            // Do NOT add ContentType here
            'Access-Control-Allow-Origin': environment.headerHost,
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ResponseType: 'blob',
            Authorization: authTkn
        };
        console.log('and headers:');
        console.log(headers);
        const response = await this.http
            .post(
                url,
                params,
                (Headers = {
                    ...this.urlProvider.baseGetOptions(),
                    headers
                })
            )
            .subscribe((response) => {
                console.log('work complete');
                console.log(response);
            });
    }

    public async getCoeSearchXLSViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCity: data['chosenCity'],
            chosenRecruiterName: data['chosenRecruiterName'],
            chosenIntervieweeName: data['chosenIntervieweeName'],
            chosenSignatureDateStart: data['chosenSignatureDateStart'],
            chosenSignatureDateEnd: data['chosenSignatureDateEnd'],
            chosenCoeStatus: data['chosenCoeStatus'],
            export_type: data['strExportType'],
            bWithMSD: data['bWithMSD'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            coes: JSON.parse(data['coes']),
            coedetail: JSON.parse(data['coedetail']),
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrlCoeSearchXLS();
        //getUrlCoeSearchXLS
        console.log('running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestCOESearch(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/xlsx',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getChildCountsPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        console.log('calling service getChildCountsPost in api-msin-exports service via GET:');
        console.log(data);
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';
        console.log('auth obj:');
        console.log(authObject);
        console.log(authObject.headers.lazyUpdate[2].value);
        console.log(authTkn);

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlChildCountsExportData();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            chosenPeriodDateStart: data['chosenPeriodDateStart'],
            chosenPeriodDateEnd: data['chosenPeriodDateEnd'],
            chosenPerformancePeriod: data['chosenPerformancePeriod'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            counts: data['counts'],
            countsDetail: data['countsDetail'],
            defaultColumns: data['defaultColumns'],
            districtsCounts: data['districtsCounts'],
            unduplicatedCounts: data['unduplicatedCounts'],

            fileNameLabel: data['file_name_label'],

            coeIDs: data['coes'],
            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestChildReportExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getEnrollmentsPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        if (data['csvOption']) {
            blobType = 'csv';
        }
        const url = this.urlProvider.getUrlEnrollmentsExportData();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            countRegions: data['countRegions'],
            countCounties: data['countCounties'],
            countDistricts: data['countDistricts'],
            countSchools: data['countSchools'],
            countEnrollments: data['countEnrollments'],
            countChildren: data['countChildren'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            schools: data['schools'],
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths'],
            defaultColumns: data['defaultColumns'],
            selectedAdditionalColumns: data['selectedColumns'],
            csvOption: data['csvOption']
        };
        console.log('running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            //await this.makeRequestEnrollmentsExportData(url, 'POST', headers, JSON.stringify(params), blobType, params.strFileName, params.fileNameLabel, params.template_name, true, params.columnWidths, params.csvOption);
            return this.makeRequestEnrollmentsExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths,
                params.csvOption
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getServiceReportPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlServiceReportsExportData();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            allCountiesObj: data['allCountiesObj'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            schoolsLabel: data['schoolsLabel'],
            schoolsInd: data['schoolsInd'],
            ind: data['ind'],
            reportType: data['reportType'],
            export_type: data['strExportType'],
            exportType: data['exportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            schools: data['schools'],
            results: data['results'],
            students: JSON.parse(data['students']),
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnHeadersSummary: data['columnHeadersSummary'],
            columnWidths: data['columnWidths'],
            iTotalDistrictsCount: data['iTotalDistrictsCount'],
            bExportDebugMode: data['bExportDebugMode']
        };
        console.log('running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            let strRegion: string = '';

            const regions = data['chosenRegions'] as Region[] | undefined;
            if (regions && regions.length > 0 && regions[0]?.CODE) {
                strRegion = regions[0].CODE;
            }

            await this.makeRequestServiceReportExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths,
                strRegion
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getAcademicRiskPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlAcademicRiskExportData();

        let bIsChosenSchool: boolean = data['isChosenSchool'];
        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            isChosenSchool: bIsChosenSchool,
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            schools: data['schools'],
            fileNameLabel: data['fileNameLabel'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestAcademicRiskExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths,
                bIsChosenSchool
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getInaPdfPost(data: any): Promise<string> {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getInaPdfExportData();

        let bIsChosenSchool: boolean = data['isChosenSchool'];
        var params = {
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            bOpen: data['bOpen'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            strFileName: data['strFileName'],
            inaSchoolEnrollment: data['inaSchoolEnrollment'],
            schoolEnrollment: data['schoolEnrollment'],
            lpRecommendedServices: data['lpRecommendedServices'],
            lpRecommendedSupportServices: data['lpRecommendedSupportServices'],
            fileNameLabel: data['fileNameLabel'],
            authObj1: JSON.stringify(authObject)
        };

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };
        let latestFileName: string;
        try {
            latestFileName = await this.makeRequestInaPdfExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                bIsChosenSchool,
                params.bOpen
            );
            return latestFileName;
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getPFSPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlPFSExportData();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            schools: data['schools'],
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestPFSData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getINAPrintViewPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlInaViewPrintExportData();

        var params = {
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            contentToExport: data['contentToExport'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            inaSchoolEnrollment: data['inaSchoolEnrollment'],
            inaSchoolEnrollmentMessage: data['inaSchoolEnrollmentMessage'],
            inaSchoolEnrollmentSections: data['inaSchoolEnrollmentSections'],
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestINAViewData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }
    public async getINAViewPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlInaViewExportData();

        var params = {
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            inaSchoolEnrollment: data['inaSchoolEnrollment'],
            inaSchoolEnrollmentMessage: data['inaSchoolEnrollmentMessage'],
            inaSchoolEnrollmentSections: data['inaSchoolEnrollmentSections'],
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestINAViewData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getGraduationPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlGraduationExportData();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            schools: data['schools'],
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestGraduationData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getInaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlInaExportData();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            schools: data['schools'],
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestInaData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getCommunicationEventPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data['strExportType'] == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlCommunicationEventExportData();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['schoolYear'],
            chosenPerformancePeriod: data['schoolYear'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            schools: data['schools'],
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestCommunicationEventExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.strFileName,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getChildCountsRegionalExportDataPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            chosenRegions: data['chosenRegions'],
            lstSelectedRegion: data['lstSelectedRegion'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            chosenPerformancePeriod: data['performancePeriod'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            strFileName: data['strFileName'],
            counts: data['counts'],
            countsDetail: data['countsDetail'],
            fileNameLabel: data['file_name_label'],
            reportType: data['reportType'],
            authObj1: JSON.stringify(authObject),
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };
        console.log('step1:');
        let blobType: string = 'application/xlsx';
        if (data['export_type'] == 'PDF') {
            blobType = 'pdf';
        }
        console.log('step2:');
        const url = this.urlProvider.getUrlChildCountsExportData();

        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestChildReportExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                data.strFileName,
                data.fileNameLabel,
                data.template_name,
                true,
                data.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getCoeReviewLogExportDataPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        console.log('chosenCoeStatus = ');
        console.log(data['chosenCoeStatus']);
        let tmpChosenCoeStatus: string[];
        if (data['chosenCoeStatus'] == '') {
            tmpChosenCoeStatus = [];
        } else {
            tmpChosenCoeStatus = data['chosenCoeStatus'];
        }
        let tmpChosenCoeVersion: string[];
        if (data['chosenCoeVersion'] == '') {
            tmpChosenCoeVersion = [];
        } else {
            tmpChosenCoeVersion = data['chosenCoeVersion'];
        }

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCity: data['chosenCity'],
            chosenRecruiterName: data['chosenRecruiterName'],
            chosenReviewerName: data['chosenReviewerName'],
            chosenSignatureDateStart: data['chosenSignatureDateStart'],
            chosenSignatureDateEnd: data['chosenSignatureDateEnd'],
            chosenCoeStatus: tmpChosenCoeStatus,
            chosenCoeVersion: tmpChosenCoeVersion,
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            coes: data['coes'],
            coedetail: data['coedetail'],
            fileNameLabel: data['file_name_label'],

            coeIDs: data['coes'],
            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        let blobType: string = 'application/xlsx';
        if (params.export_type == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlCoeReviewLogExportData();

        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestCOEReviewLogExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getCoeReviewLogDataPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';
        console.log('chosenCoeStatus = ');
        console.log(data['chosenCoeStatus']);
        let tmpChosenCoeStatus: string[];
        if (data['chosenCoeStatus'] == '') {
            tmpChosenCoeStatus = [];
        } else {
            tmpChosenCoeStatus = data['chosenCoeStatus'];
        }
        let tmpChosenCoeVersion: string[];
        if (data['chosenCoeVersion'] == '') {
            tmpChosenCoeVersion = [];
        } else {
            tmpChosenCoeVersion = data['chosenCoeVersion'];
        }

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCity: data['chosenCity'],
            chosenRecruiterName: data['chosenRecruiterName'],
            chosenReviewerName: data['chosenReviewerName'],
            chosenIntervieweeName: data['chosenIntervieweeName'],
            chosenSignatureDateStart: data['chosenSignatureDateStart'],
            chosenSignatureDateEnd: data['chosenSignatureDateEnd'],
            localExportDateTime: data['localExportDateTime'],
            chosenCoeStatus: tmpChosenCoeStatus,
            chosenCoeVersion: tmpChosenCoeVersion,
            chosenCoeNumber: data['chosenCoeNumber'],
            export_type: data['strExportType'],
            bWithMSD: data['bWithMSD'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            coes: data['coes'],
            coedetail: data['coedetail'],
            fileNameLabel: data['file_name_label'],
            reporttype: data['reporttype'],

            coeIDs: data['coes'],
            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths'],
            columnHeaders2017: data['columnHeaders2017'],
            columnWidths2017: data['columnWidths2017']
        };

        let blobType: string = 'application/xlsx';
        if (params.export_type == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlCoeReviewLogExportData();

        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestCOEReviewLogExportData(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                blobType,
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports:' + Err);
            throw Err;
        }
    }

    public async getCoeExportDataPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCity: data['chosenCity'],
            chosenRecruiterName: data['chosenRecruiterName'],
            chosenIntervieweeName: data['chosenIntervieweeName'],
            chosenSignatureDateStart: data['chosenSignatureDateStart'],
            chosenSignatureDateEnd: data['chosenSignatureDateEnd'],
            localExportDateTime: data['localExportDateTime'],
            chosenCoeStatus: data['chosenCoeStatus'],
            chosenCoeNumber: data['chosenCoeNumber'],
            export_type: data['strExportType'],
            bWithMSD: data['bWithMSD'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            coes: data['coes'],
            fileNameLabel: data['file_name_label'],
            reporttype: data['reporttype'],

            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        let blobType: string = 'application/xlsx';
        if (params.export_type == 'PDF') {
            blobType = 'pdf';
        }
        const url = this.urlProvider.getUrlCoeExportData();

        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            return this.makeRequestCOEExportData(
                url,
                'POST',
                headers,
                params,
                blobType,
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports: ' + Err);
            throw Err;
        }
    }

    public async getCoePrintBulkExportDataPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            regions: data['regions'],
            chosenSignatureDateStart: data['chosenSignatureDateStart'],
            chosenSignatureDateEnd: data['chosenSignatureDateEnd'],
            chosenCoeNumbers: data['chosenCoeNumbers'],
            export_type: data['strExportType'],
            bWithMSD: data['bWithMSD'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            coes: data['coes'],
            coedetail: JSON.stringify(data['coedetail']),
            coedetail2: JSON.stringify(data['coedetail']),
            coedetail3: JSON.stringify(data['coedetail3']),
            fileNameLabel: data['file_name_label'],
            reporttype: data['reporttype'],
            resultsCoeNode: JSON.stringify(data['resultsCoeNode']),
            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths'],
            bOpen: data['bOpen']
        };

        let blobType: string = 'application/xlsx';
        if (params.export_type == 'PDF') {
            blobType = 'pdf';
        }
        let url = this.urlProvider.getUrlCoePrintBulkExportData();
        console.log('bOpen check:');
        if (params.bOpen) {
            console.log(params.bOpen);
            url = this.urlProvider.getUrlCoePrintBulkViewData();
            console.log('we will be running print-viewer url:');
            console.log(url);
        }

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestCOEBulkExportData(
                url,
                'POST',
                headers,
                params,
                blobType,
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths,
                params.bOpen
            );
        } catch (Err) {
            console.log('caught the error also in api-exports: ' + Err);
            throw Err;
        }
    }

    public async getServiceStudentsDataPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        let blobType: string = 'application/xlsx';
        if (data.export_type == 'PDF') {
            blobType = 'pdf';
        }
        const url = EndPoints.studentServiceLists_GetStudentsByPartyService;

        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(data);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestServiceStudentData(
                url,
                'POST',
                headers,
                JSON.stringify(data),
                blobType,
                data.file_name,
                data.fileNameLabel,
                data.template_name,
                true,
                data.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports: ' + Err);
            throw Err;
        }
    }

    public async getDtMonEnrollmentsXLSViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['strYear'],
            performancePeriod: data['strYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            studentIds: JSON.parse(data['studentIds']),
            fileNameLabel: data['file_name_label'],
            grade: data['grade'],
            age: data['age'],
            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrl4DtMonEnrollmentsXLS();
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            // Do NOT add ContentType here
            'Access-Control-Allow-Origin': environment.headerHost,
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ResponseType: 'blob',
            Authorization: authTkn
        };

        try {
            await this.makeRequestDtMonEnrollments(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/xlsx',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.schoolYear,
                true,
                params.columnWidths
            );
        } catch (Err) {
            console.log('caught the error also in api-exports');
            throw Err;
        }
    }

    public async getDtMonEnrollmtAndServiceXLSViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['strYear'],
            performancePeriod: data['strYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            studentIds: JSON.parse(data['studentIds']),
            fileNameLabel: data['file_name_label'],
            grade: data['grade'],
            age: data['age'],
            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrl4DtMonEnrollmtsAndService();
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestDtMonEnrollments(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/xlsx',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.schoolYear,
                true,
                params.columnWidths
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getDtMonSimplifiedEnrollmtXLSViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            chosenSchools: data['chosenSchools'],
            schoolYear: data['strYear'],
            performancePeriod: data['strYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            studentIds: JSON.parse(data['studentIds']),
            fileNameLabel: data['file_name_label'],
            grade: data['grade'],
            age: data['age'],
            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrl4DtMonSimplifiedEnrollmt();
        console.log('now running post with url:');
        console.log(url);
        console.log('and params set:');
        console.log(params);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };

        try {
            await this.makeRequestDtMonEnrollments(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/xlsx',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.schoolYear,
                true,
                params.columnWidths
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getSSDPGeneralXLSViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();

        var params = {
            region_category: data['strRegionCategory'],
            district_id: '',
            year: data['strYear'],
            msd_number: '',
            export_type: data['strExportType'],
            second_last_name: '',
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['Template_Name'],
            file_name: data['strFileName'],
            dob: '',
            sex: '',
            ssid: '',
            studentIds: JSON.parse(data['studentIds']),
            fileNameLabel: data['fileNameLabel'],
            grade: data['grade'],
            age: data['age'],
            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrl4GeneralSSDP(EndPoints.apiMsinExportsSSDPGeneral);
        console.log('now running post with url:');
        console.log(url);
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';
        console.log('auth obj:');
        console.log(authTkn);
        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authTkn
        };
        try {
            await this.makeRequest(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/xlsx',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.year
            );
        } catch (Err) {
            throw Err;
        }
    }

    public getSSDPGeneralXLS(data: any) {
        const url = this.urlProvider.getUrl2SSDPGeneral(EndPoints.apiMsinExportsSSDPGeneral, '', '', '');
        const authObject = this.urlProvider.baseGetOptions();

        let retObj: any;
        console.log('url:' + url);

        let urlParam = '?';

        urlParam += 'strRegionCategory=' + (data.strRegionCode ? data.strRegionCode : '');
        urlParam += '&strDistrictId=' + (data.strDistrictId ? data.strDistrictId : '');
        urlParam += '&strYear=' + (data.strYear ? data.strYear : '');
        urlParam += '&strTemplateName=' + (data.strTemplateName ? data.strTemplateName : '');
        urlParam += '&strExportType=XLS';
        urlParam += '&bCheckedStyling=' + data.bCheckedStyling;
        urlParam += '&strHtmlContent=' + data.htmlContent;
        urlParam += '&authObj1=' + JSON.stringify(authObject);
        urlParam += '&studentIds=' + (data.studentIds ? data.studentIds : '');
        let newurl = url + '' + urlParam;
        window.location.replace(newurl);
    }

    public getSSDPReportXLS(data: any) {
        const url = this.urlProvider.getUrl2(EndPoints.apiMsinExportsXLS, '', '', '');
        const authObject = this.urlProvider.baseGetOptions();

        let retObj: any;
        console.log('url:' + url);

        let urlParam = '?';
        urlParam += 'strRegionCategory=' + (data.strRegionCode ? data.strRegionCode : '');
        urlParam += '&strDistrictId=' + (data.strDistrictId ? data.strDistrictId : '');
        urlParam += '&strYear=' + (data.strYear ? data.strYear : '');
        urlParam += '&strTemplateName=' + (data.strTemplateName ? data.strTemplateName : '');
        urlParam += '&strExportType=XLS';
        urlParam += '&bCheckedStyling=' + data.bCheckedStyling;
        urlParam += 'strHtmlContent=' + data.htmlContent;
        urlParam += '&authObj1=' + authObject;
        let newurl = url + '' + urlParam;
        window.location.replace(newurl);
    }

    triggerTimeoutReset() {
        console.log('now calling triggerTimeout');
        this.timeoutResetSubject.next(undefined);
    }

    get timeoutResetObservable(): Observable<void> {
        return this.timeoutResetSubject.asObservable();
    }

    public async makeRequest(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        periodYears: string
    ) {
        console.log('now replacing ' + periodYears + ' from ' + fileName);
        if (fileName.includes(periodYears)) {
            if (fileNameLabel.includes(periodYears)) {
                fileName = fileName.replace('(' + periodYears + ')', '');
            }
        }
        console.log('now calling makeRequest for filename:');
        console.log(fileName);

        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        // Kick off the main http post to get the export data
        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        const response = await this.http.post(url, body, httpOptions).subscribe(
            //httpOptions
            (response: any) => {
                console.log('step2');
                clearInterval(timer);
                console.log('step3');
                const blob = new Blob([response], { type: blobType });

                console.log('positive response recieved');
                console.log(response);
                console.log('blobType:');
                console.log(blobType);
                if (blobType == 'application/xlsx') {
                    const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                    const url = URL.createObjectURL(renamedFile);

                    const a = document.createElement('a');
                    a.href = url;
                    fileNameLabel = fileNameLabel.trimLeft();
                    fileName = fileName.trimLeft();
                    console.log('will overwrite current and name the file ' + template_name + '-' + fileNameLabel + ':' + fileName + '.xlsx');
                    a.download = template_name + '-' + fileNameLabel + fileName + '.xlsx';
                    a.click();
                    if (a && a.parentNode === document.body) {
                        document.body.removeChild(a);
                    }

                    URL.revokeObjectURL(url);
                } else {
                    const url = URL.createObjectURL(blob);

                    const a = document.createElement('a');
                    a.href = url;
                    fileNameLabel = fileNameLabel.trimLeft();
                    fileName = fileName.trimLeft();
                    console.log('will overwrite current and name the file ' + template_name + '-' + fileNameLabel + ':' + fileName + '.pdf');
                    a.download = template_name + '-' + fileNameLabel + fileName + '.pdf';
                    a.click();
                    if (a && a.parentNode === document.body) {
                        document.body.removeChild(a);
                    }

                    URL.revokeObjectURL(url);
                }
            },
            (error) => {
                clearInterval(timer);

                console.log('Error in makeRequest of api-exports.service:' + JSON.stringify(error));
                throw error;
            }
        );
    }

    public async makeRequestDtMonGradeAge(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        periodYears: string,
        bAddTimeStamp: boolean,
        strHtml: string
    ) {
        if (fileName.includes(periodYears)) {
            if (fileNameLabel.includes(periodYears)) {
                fileName = fileName.replace('(' + periodYears + ')', '');
            }
        }
        console.log('now calling makeRequestDtMonGradeAge for filename:');
        console.log(fileName);

        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(url);
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };
        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('positive response recieved');
                    console.log(response);
                    console.log('blobType:');
                    console.log(blobType);
                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileNameLabel + ':' + fileName + '.pdf');
                        a.download = fileNameLabel + fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    clearInterval(timer);

                    console.log('Error in makeRequestDtMonGradeAge of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestCOEExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);
                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        fileName = fileName.trimLeft();

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestCOEExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    fnWriteCssForPdfExport(): string {
        let retStr = `     <style>.full-width {           width: 40%; display: inline-block;        }
            body {    font-family: Arial, sans-serif;}
            .left { position:absolute; top:0px; left:0px;}   
            .right {    text-align:right; }
            .custom-table1-mixed td div.nopad{    padding-left:0px !important; }
            .custom-table1-mixed {   border: 1px solid black;padding-left: 5px; padding-bottom:0px;padding-top:5px;       margin: 0;   border-collapse: collapse; width:782px; height: 100% !important;    } //690px
            .custom-table1-mixed, .custom-table1-mixed td {      font-size:9px; padding-left:5px;           }


            .custom-table1 td div.nopad{    padding-left:0px !important; }
            .custom-table1 { border: 1px solid black;padding:0;     margin: 0;  left:500px; border-collapse: collapse; width:100%;      }
            .custom-table1, .custom-table1 td {      font-size:9px; padding-left:5px;           }
           
            .custom-tableOne2023Mixed td div.nopad{    padding-left:0px !important; }
            .custom-tableOne2023Mixed {    border: 1px solid black;padding: 5px;       margin: 0;    border-collapse: collapse; width:100%; height:975px;width:752px; }
            .custom-tableOne2023Mixed, .custom-tableOne2023Mixed td {      font-size:11px; padding-left:5px;  padding-top:0px; padding-bottom:0px;         }
           
            .custom-tableOne2023 td div.nopad{    padding-left:0px !important; }            
            .custom-tableOne2023 {    border: 1px solid black;padding: 0;       margin: 0;   width:100%; border-collapse: collapse; width:815px;  }           //position:absolute;height:700px; 
            .custom-tableOne2023, .custom-tableOne2023 td {      font-size:1.2em; padding-left:5px;  padding-top:0px; padding-bottom:0px;           }


            .custom-tableOne2023a {    border: 1px solid black;padding: 5px;       margin: 0;    width:600px   ;border-collapse: collapse;   }
            .custom-tableOne2023a, .custom-tableOne2023a td {      background-color:orange; padding-left:5px;  height:825px !important;  }//  position:absolute;     font-size:13px; 
            .custom-tableOne2023b td div.nopad{    padding-left:0px !important;  }
            .custom-tableOne2023b {    margin-left:15px;border: 1px solid black;padding: 0px;     margin: 0;   border-collapse: collapse; width:100%; width:770px; } // position:absolute; height:915px height:825px;
			//.custom-tableOne2023b, .custom-tableOne2023b tr {      height:16px;          }
            .custom-tableOne2023b, .custom-tableOne2023b td {      font-size:11px; padding-left:5px;  padding-top:0px; padding-bottom:0px;margin:0;         }

            .custom-table3 {    padding: 5px;       margin: 0; border-collapse: collapse; width:100%      }
            .custom-table3, .custom-table3 td {      font-size:6px; padding-left:5px;            }
            .custom-tableThree2023 {    border: 1px solid black;padding: 5px;       margin: 0; border-collapse: collapse; width:100%      }
            .custom-tableThree2023, .custom-table3 td {      font-size:9px; padding-left:5px;            }

            .custom-table4 {   font-size:9px;padding: 5px;    margin: 0; border-collapse: collapse; width:100%;     }
            .custom-table4 td {      font-size:9px; border:1px solid black;  padding-left:5px;           }
            .custom-tableFour2023 {    padding: 5px;    margin: 0; border-collapse: collapse;   }
            .custom-tableFour2023 td {      font-size:16px; border:1px solid black;  padding-left:5px;           }

            .no-border td {      border:none;     outline: none;      }
            .header-cell {          background-color:#d3d3d3;  border:1px solid black;  padding-top:3px; padding-bottom:3px; vertical-align:top;    }
            .header-cell-inner {          background-color:#d3d3d3; border:1px solid black;    padding-top:3px; padding-bottom:3px;     }
            .header-text {           font-size: 13px; font-weight: bold;      }
            .value-text {           font-size: 6px;      }
            .header-cell2023 {          background-color:#d3d3d3;  border:1px solid black;  padding-top:3px;  height:62px;   }
            .header-cell-inner2023 {          background-color:#d3d3d3; border:1px solid black;    padding-top:3px; padding-bottom:3px;     }
            .header-text2023 {           font-size: 16px; font-weight: bold;      }
            .value-text2023 {           font-size: 18px;      }
            .value-text-inner {           border:1px solid black;      }
            .header-cellcent2023 {     text-align:center;border:1px solid black;          }
            .value-text-inner2023 {          border:1px solid black;        }
            .tr:first-child {           background-color:   #d3d3d3     }            
            .font2023 {           font-size:14px;        }
            @media screen { .stdline-2023 {margin:0px;padding:0px;line-height:.5em;} .stdline2-2023 {margin:0px;padding:0px;line-height:22px;}  .stdline3-2023a { margin: 0px; padding: 0px; line-height:12px; } .stdline3-2023 { margin: 0px; padding: 0px; line-height:4px; }
                .stdline5a-2023 {margin:0px;padding:0px;line-height:.31em;}
                .stdline32023text {margin-top:-1.4em;line-height:1.5em;margin-left:5px;}
                .stdline-2023b { margin: 0px; padding: 0px; line-height:2.05em; }
                .stdline-2023sc { margin: 0px; padding: 0px; line-height:.1em; }
                .stdline-2023c { margin: 0px; padding: 0px; line-height:.1em; }
                .stdline4-2023 { margin: 0px; padding: 0px; line-height:2.1em; } 
                .stdline5-2023 { margin: 0px; padding: 0px; line-height:38px; }  stdline5b-2023 { margin: 0px; padding: 0px; line-height:0px; }
                .stdline6-2023 { margin: 0px; padding: 0px; line-height:45px; } .stdline6a-2023 { margin: 0px; padding: 0px; line-height:6px; }
                .stdline7-2023 { margin: 0px; padding: 0px; line-height:45px; }
                .std2spacing-2017 { margin: 0px; padding: 0px; line-height:6px; }
                .std3spacing-2017 { margin: 0px; padding: 0px; line-height:3px; }
                .comments2adjust {position:absolute;margin-top:-1.7em;}
                .reset2a {margin-top:90px;clear:both;}
                .grid-item {  padding: 0px; padding: 0px; padding-left:0px; padding-right: 0px; height:100%;width:100%; } 
                .grid-item2 {  padding: 0px; padding: 0px;  padding-left:0px; padding-right: 0px; height:100% !important;width:607px; }  
                .grid-item-mixed {  color: white; padding: 0;vertical-align:top; margin-top:0;m.8argin-right:0; margin-bottom:0;height:725px;  }
                .grid-item2023a {    padding: 0px; padding: 0px; padding-left:0px; padding-right: 0px; line-height:1.4em;   }  /*  padding: 0px; margin:0px; */
                .grid-item2023b {  padding: 0px;padding: 0px; padding-left:0px; padding-right: 0px;    } 
                .grid-container2017 {  display: grid; grid-template-columns: 52% 47%; width:1145px; height:620px; padding: 0px; margin-right:0;gap: 0px;   }     
                .grid-container2017mixed {  display: grid; grid-template-columns: 52% 47%; height:915px; width:100%; width:1520px;padding: 0px; gap: 0px;   } 
                .grid-container2023 {  display: grid;grid-template-columns: 52% 47%; height:925px;  padding: 0px; gap: 0px;   }  .p-screenMoves2017 {line-height:14.5px;}  
                .grid-container2023all {   height:40vw; padding: 0px;margin:0;width:120vw; !important;  } 
                .createdHeader2023 {margin-top:30px;}
                .positionScTxt {margin-top:-4px;;z-index:4000; }
                .footerposition2023 {position:absolute;padding-top:900px;left:0px;} 
                .comments-adjust-2017 { padding-top:1.4em;line-height:1.4;} .text-adjust2-2017 {position:absolute;margin-top:-1.1em;} .text-adjust2-2017-mixed {position:absolute;margin-top:-2em;padding-top:5px; margin-right:10px;} 
                .text-adjust3-2017 {position:absolute;left:5px;margin-top:-8px;}
                .text-adjust-2017 { position:absolute; margin-left:5px;margin-top:0px;} .regular-text { font-size:14px; } .small-text { font-size:10px; } .smaller-text {font-size:10px;} 
                .text-adjust-2023 { margin-top:8px;}
                .custom-textareaFour2023 { margin-top:4px;} .p-screen-vs-print2017 {line-height:.1em;  }  
                .pprint2017comments {line-height:.1em; margin:0;padding:0;} 
                .pprint2017comments2 {line-height:.1em;padding:0;margin:0; }
                 .p-screen-vs-print2 {line-height:3px; } p2017footerspace {line-height:0px; } .p-screen-vs-print3 {line-height:7px; margin:0; padding:0; } .p-screenFooter {line-height:20px; }  .textBoxAdjust { margin-top:17px;} 
                 .text-box-2023 { width:130px; line-height:1.4em; border: 0px solid black; padding: 0px; width: fit-content;text-align:justify;} 
                 .text-box2-2023 { line-height:1.7em; text-indent:0; left:5px; }
                 .text-box3-2023 { width:140px; line-height:1.7em; border: 0px solid black; padding: 0px; width: fit-content;text-align:justify;} 
                 .media-p2-padheader {margin:0; padding:0;height:0em !important;z-index:4000;} .mediaVsPrintPadding2 {margin:0; padding:0;} .mediaVsPrintPadding {'padding-bottom:6px;'} 
                 .textbox3a2023adjust {margin-top:0px;} text-box-2017a { border: 0px solid black; padding: 0px; width: fit-content;text-align:left;word-wrap:break-word;}
                 .mediaPNoPadding {margin:0; padding:0;padding-bottom:0px;line-height:2px;} .mediaP2Padding {margin:0; padding:0;padding-bottom:0px;line-height:10px;} 
                 .mediaP3Padding-2023  {margin:0; padding:0;line-height:0px;} 
                 .mediaP7Padding-2023 {margin:0; padding:0;padding-bottom:0px;line-height:0px;}
                 .flushbottom-2023 {padding:0;margin:0; line-height:0px;}
                 .flushbottom-2023-2 {padding:0;margin:0; line-height:3px;} 
                 .mediaP6Padding {margin:0; padding:0;padding-bottom:0px;line-height:7px;}} 
                 .positionfooter-2017 {position:absolute;top:700px; left:0px;z-index:4000;} 
                 .positionfooter-2017mixeda {position:absolute;top: 101%; left:0;z-index:4000;} 
                 .positionfooter-2017mixedb {position:absolute;top: 101%; right:35px;z-index:4000;}
            .smaller-text-2023 {           font-size:9px;        }
            .smaller-text-2023header  {           font-size:10px;        }
            .smaller-text-2017 {           font-size:8px;        }
            .checkbox-small {        width: 10px;        height:10px;   transform: scale(1.2);   margin-right: 5px;  cursor: default; pointer-events:none;     }
            .checkbox {        width: 12px;        height:12px;    margin-right: 5px;  cursor: default; pointer-events:none;     } // transform: scale(2.2); height:12px;
            .underline6-2017 {        position: absolute; margin-top:-2px;height: 1px; background-color: black; left: 5px; width:480px; }// #E0E0E0 //#D3D3D3;
            .underline6-2017:nth-child(3) {    position: absolute;   top: calc(3.3em); height: .1px; border-bottom: .1px solid black;} // #E0E0E0;
            .underline6-2017:nth-child(4) {    position: absolute;    top: calc(5.3em); height: .1px; border-bottom: .1px solid black;} // #E0E0E0;
            .underline6-2017:nth-child(5) {    position: absolute;     top: calc(7.4em); height: .1px; border-bottom: .1px solid black;} // #E0E0E0;
            .underline6firstrow2017 {        position: absolute; height: 1px; background-color: black; left: 5px; width:480px;   }// //#D3D3D3;

            .underline6firstrow {        position: absolute; height: 1px; background-color: #E0E0E0; left: 5px; width:480px;   }// //#D3D3D3;
            .underline6 {        position: absolute; height: 1px; background-color: #E0E0E0; left: 5px; width:480px; }// #E0E0E0 //#D3D3D3;
            .underline6:nth-child(1) {      position: absolute;  top: calc(1.2em);  background-color:#E0E0E0;} // #E0E0E0;
            .underline6:nth-child(2) {      position: absolute;  top: calc(2.2em); background-color:#E0E0E0;} // #E0E0E0;
            .underline6:nth-child(3) {      position: absolute;  top: calc(3.8em);  background-color:#E0E0E0;} //#E0E0E0;
            .underline6:nth-child(4) {      position: absolute;  top: calc(5.5em);  background-color:#E0E0E0;}    // #E0E0E0
            .underline6:nth-child(5) {      position: absolute;  top: calc(7.1em);  background-color:#E0E0E0;}   // #E0E0E0
            .underline6:nth-child(6) {      position: absolute;  top: calc(9.1em);  background-color:#E0E0E0;}  // #E0E0E0

            .underline-2017 {        position: absolute; height: 1px; background-color: black; left: 5px; width:540px;   }//#D3D3D3;
            .underlinefirstrow-2017 {        position: absolute; height: 1px; background-color: black; left: 5px; width:540px;   }//#D3D3D3;
            .underline-2017:nth-child(1) {      position: absolute;  top: calc(1.2em);  background-color:black; }
            .underline-2017:nth-child(2) {      position: absolute;  top: calc(2.2em); background-color:black;}
            .underline-2017:nth-child(3) {      position: absolute;  top: calc(3.8em);  background-color:black;}
            .underline-2017:nth-child(4) {      position: absolute;  top: calc(5.5em);  background-color:black;}
            .underline-2017:nth-child(5) {      position: absolute;  top: calc(7.1em);  background-color:black;}


            .underline {        position: absolute; height: 1px; background-color: #E0E0E0; left: 5px; width:540px;   }//#D3D3D3;
            .underlinefirstrow {        position: absolute; height: 1px; background-color: #E0E0E0; left: 5px; width:540px;   }//#D3D3D3;
            .underline:nth-child(1) {      position: absolute;  top: calc(1.2em);  background-color:#E0E0E0; }
            .underline:nth-child(2) {      position: absolute;  top: calc(2.2em); background-color:#E0E0E0;}
            .underline:nth-child(3) {      position: absolute;  top: calc(3.8em);  background-color:#E0E0E0;}
            .underline:nth-child(4) {      position: absolute;  top: calc(5.5em);  background-color:#E0E0E0;}
            .underline:nth-child(5) {      position: absolute;  top: calc(7.1em);  background-color:#E0E0E0;}

            .underlineComment {        position: absolute; height: 1px; background-color: #E0E0E0; left: 5px; width:520px;   }//#D3D3D3;
            .underlinefirstrowComment {        position: absolute; height: 1px; background-color: #E0E0E0; left: 5px; width:520px;   }//#D3D3D3;
            .underlineComment:nth-child(1) {      position: absolute;  top: calc(1.2em);  background-color:#E0E0E0; }
            .underlineComment:nth-child(2) {      position: absolute;  top: calc(2.2em); background-color:#E0E0E0;}
            .underlineComment:nth-child(3) {      position: absolute;  top: calc(3.8em);  background-color:#E0E0E0;}
            .underlineComment:nth-child(4) {      position: absolute;  top: calc(5.5em);  background-color:#E0E0E0;}
            .underlineComment:nth-child(5) {      position: absolute;  top: calc(7.1em);  background-color:#E0E0E0;}
            .underlineComment:nth-child(6) {      position: absolute;  top: calc(8.5em);  background-color:#E0E0E0;}
            .underlineComment:nth-child(7) {      position: absolute;  top: calc(10.0em);  background-color:#E0E0E0;}
            .underlineComment:nth-child(8) {      position: absolute;  top: calc(11.5em);  background-color:#E0E0E0;}
            .underlineComment:nth-child(9) {      position: absolute;  top: calc(13.0em);  background-color:#E0E0E0;}
            .underlineComment:nth-child(10) {      position: absolute;  top: calc(14.5em);  background-color:#E0E0E0;}


            .underlineComment2017lg {                   position: absolute; height: 1px; background-color: rgba(0, 0, 0, 0.5); left: 0px; width:100%;   }
            .underlineComment2017lg:nth-child(1) {      position: absolute;  top: calc(0em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(2) {      position: absolute;  top: calc(1.6em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(3) {      position: absolute;  top: calc(3.2em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(4) {      position: absolute;  top: calc(4.8em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(5) {      position: absolute;  top: calc(6.4em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(6) {      position: absolute;  top: calc(8.0em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(7) {      position: absolute;  top: calc(9.6em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(8) {      position: absolute;  top: calc(11.2em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(9) {      position: absolute;  top: calc(12.8em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(10) {      position: absolute;  top: calc(14.4em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017lg:nth-child(11) {      position: absolute;  top: calc(16.0em); background-color: rgba(0, 0, 0, 0.5); }


            .underlineComment2017 {                   position: absolute; height: 1px; background-color: rgba(0, 0, 0, 0.5); left: 5px; width:520px;   }//#D3D3D3;
            .underlinefirstrowComment2017 {        position: absolute; height: 1px; background-color: rgba(0, 0, 0, 0.5); left: 5px; width:520px;   }//#D3D3D3;
                                                                                                                                                                              //  .underlineComment2017:nth-child(1) {      position: absolute;  top: calc(1.2em);  background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(2) {      position: absolute;  top: calc(2.2em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(3) {      position: absolute;  top: calc(3.8em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(4) {      position: absolute;  top: calc(5.5em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(5) {      position: absolute;  top: calc(7.1em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(6) {      position: absolute;  top: calc(8.5em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(7) {      position: absolute;  top: calc(10.0em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(8) {      position: absolute;  top: calc(11.5em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(9) {      position: absolute;  top: calc(13.0em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017:nth-child(10) {      position: absolute;  top: calc(14.5em); background-color: rgba(0, 0, 0, 0.5); }

            .underlineComment2017med {                   position: absolute; height: 1px; background-color: transparent;  left: 0px; width:100%;   }// rgba(0, 0, 0, 0.5)#D3D3D3;545px
            .underlineComment2017med:nth-child(1) {      position: absolute;  top: calc(0em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(2) {      position: absolute;  top: calc(1.6em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(3) {      position: absolute;  top: calc(3.2em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(4) {      position: absolute;  top: calc(4.8em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(5) {      position: absolute;  top: calc(6.4em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(6) {      position: absolute;  top: calc(8.0em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(7) {      position: absolute;  top: calc(9.6em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(8) {      position: absolute;  top: calc(11.2em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(9) {      position: absolute;  top: calc(12.8em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(10) {      position: absolute;  top: calc(14.4em); background-color: rgba(0, 0, 0, 0.5); }
            .underlineComment2017med:nth-child(11) {      position: absolute;  top: calc(16.0em); background-color: rgba(0, 0, 0, 0.5); }


            .underline2023 {      position:relative; left:4px; border-bottom: 1.8px solid #E0E0E0;  width:98%;   }
            .underline2023:nth-child(1) {        top: calc(0.1em);  border-bottom: 1.8px solid #E0E0E0;; }
            .underline2023:nth-child(2) {        top: calc(1.6em); border-bottom: 1.8px solid #E0E0E0;; }
            .underline2023:nth-child(3) {        top: calc(3.2em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023:nth-child(4) {        top: calc(4.8em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023:nth-child(5) {        top: calc(6.4em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023:nth-child(6) {        top: calc(8.0em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023:nth-child(7) {        top: calc(9.6em);  border-bottom: 1.8px solid #E0E0E0; }


            .underline2023last {        position: absolute; border-bottom: 1.8px solid #E0E0E0; left: 5px; width:680px;   }
            .underline2023last:nth-child(1) {      position: absolute;  top: calc(0.0em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023last:nth-child(2) {      position: absolute;  top: calc(1.6em); border-bottom: 1.8px solid #E0E0E0; }
            .underline2023last:nth-child(3) {      position: absolute;  top: calc(3.2em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023last:nth-child(4) {      position: absolute;  top: calc(4.8em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023last:nth-child(5) {      position: absolute;  top: calc(6.4em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023last:nth-child(6) {      position: absolute;  top: calc(8.0em);  border-bottom: 1.8px solid #E0E0E0; }
            .underline2023last:nth-child(7) {      position: absolute;  top: calc(9.6em);  border-bottom: 1.8px solid #E0E0E0; }


            .underline2023Comments {        position: absolute;  border-bottom:1.8px solid #E0E0E0;  left: 5px; width:735px;   } //height: 1pxbackground-color: #E0E0E0;
            
            .underline3 {       position: absolute;  height: 1px; background: #000; margin-top:18px; left: 5px; right: 5px;   }
            .underline3:nth-child(1) {      position: absolute;   height: 1px; top: calc(1em);   }
            .underline3:nth-child(2) {      position: absolute;   height: 1px; top: calc(2em);   }
            .underline4 {        position: absolute; height: 1px; background-color: #D3D3D3; left: 5px; right: 5px;   }
            .underline4:nth-child(1) {      position: absolute;  height: 1px; top: calc(1em);  background-color:#D3D3D3; } //#D3D3D3
            .underline4:nth-child(2) {      position: absolute;  height: 1px; top: calc(1.6em); background-color:#D3D3D3;}//#D3D3D3
            .underline4:nth-child(3) {      position: absolute;  height: 1px; top: calc(3.1em);  background-color:#D3D3D3;}//#D3D3D3

            .coeunderline {        display: inline-block;   width:140px;   border-bottom: 1px solid; white-space: nowrap;   }
            .centercolheader {        text-align:center;   }
            .imgspace {       margin-bottom:3px;   }
            .label-disabled {       color:gray; opacity:.6;pointer-events:none; cursor:not-allowed;   }
            .standardunderlineverylong {        display: inline-block;   width:400px;  border-bottom: 1.8px solid #E0E0E0; white-space: nowrap;  font-weight: bold;   }
            .standardunderlineverylong2017 {        display: inline-block;   width:400px;  border-bottom: 1px solid black; white-space: nowrap;  font-weight: bold;   }
            .standardunderlineverylongnotbold {        display: inline-block;   width:400px;  border-bottom: 1px solid #D0D0D0; white-space: nowrap;     }
            .standardunderlineverylongnotbold-2017 {        display: inline-block;   width:400px;  border-bottom: 1px solid black; white-space: nowrap;     }
            .standardunderlinelonger {        display: inline-block;   width:377px;  border-bottom: 1px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderlinelonger-2017 {        display: inline-block;   width:377px;  border-bottom: 1px solid black; white-space: nowrap;  font-weight: bold;   }
            .standardunderline365 {        display: inline-block;   width:365px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline365notbold {        display: inline-block;   width:365px;   border-bottom: 1.8px solid #D0D0D0; white-space: nowrap;     }
            .standardunderline350 {        display: inline-block;   width:350px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline350notbold {        display: inline-block;   width:350px;   border-bottom: 1px solid black; white-space: nowrap; }
            .standardunderline340 {        display: inline-block;   width:340px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline340notbold {        display: inline-block;   width:340px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;    }
            .standardunderline340notbold-2017 {        display: inline-block;   width:340px;   border-bottom: 1px solid black; white-space: nowrap;    }
            .standardunderline320 {        display: inline-block;   width:320px;   border-bottom: 1.8px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline320notbold-2017 {        display: inline-block;   width:320px;   border-bottom: 1px solid black; white-space: nowrap;    }
            .standardunderline320notbold {        display: inline-block;   width:320px;   border-bottom: 1.8px solid  black; white-space: nowrap;    }            
            .standardunderline320notbold-2023 {        display: inline-block;   width:320px;   border-bottom: 1.8px solid  #D0D0D0; white-space: nowrap;    }            
            .standardunderline320notboldforseasig {        display: inline-block;   width:320px;   border-bottom: 1.8px solid #E0E0E0; white-space: nowrap;    }
            .standardunderlinelong {        display: inline-block;   width:300px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline275 {        display: inline-block;   width:275px;   border-bottom: 1.8px solid #E0E0E0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline275blacknotbold {        display: inline-block;   width:275px;   border-bottom: 1.8px solid black; white-space: nowrap;    }
            .standardunderlinelong2017black {        display: inline-block;   width:300px;   border-bottom: 1px solid black; white-space: nowrap;  font-weight: bold;   }
            .standardunderline250 {        display: inline-block;   width:250px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline250notbold-2023 {        display: inline-block;   width:250px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;    }
            .standardunderline250notbold {        display: inline-block;   width:250px;   border-bottom: 1px solid #E0E0E0; white-space: nowrap;    }
            .standardunderline250blacknotbold  { display: inline-block;   width:250px;   border-bottom: 1px solid black;  white-space: nowrap; }
            .standardunderlinenotbold {        display: inline-block;   width:200px;  border-bottom: 1px solid black; white-space: nowrap;   }
            .standardunderline {        display: inline-block;   width:200px;  border-bottom: 1.8px solid #E0E0E0; white-space: nowrap; font-weight: bold;    }
            .standardunderline2017narrow {        display: inline-block;   width:200px; line-height:14px; border-bottom: 1px solid black; white-space: nowrap; font-weight: bold;    }
            .standardunderline2017 {        display: inline-block;   width:200px;  border-bottom: 1px solid black; white-space: nowrap; font-weight: bold;    }
            .standardunderline1516-2017 {        display: inline-block;   width:185px;  border-bottom: 1.5px solid black; white-space: nowrap; font-weight: bold;    }
            .standardunderline1516 {        display: inline-block;   width:185px;  border-bottom: 1px solid #D0D0D0; white-space: nowrap; font-weight: bold;    }
            .standardunderline188 {        display: inline-block;   width:183px;  border-bottom: 1px solid #D0D0D0; white-space: nowrap; font-weight: bold;    }
            .standardunderline188-2017 {        display: inline-block;   width:183px;  border-bottom: 1px solid black; white-space: nowrap; font-weight: bold;    }
            .standardunderline188-2017blacknotbold {        display: inline-block;   width:183px;  border-bottom: 1px solid black; white-space: nowrap;    }            
            .standardunderline78notbold {        display: inline-block;   width:175px;  border-bottom: 1px solid black; white-space: nowrap; }
            .standardunderline78 {        display: inline-block;   width:175px;  border-bottom: 1px solid #D0D0D0; white-space: nowrap; font-weight: bold;    }
            .standardunderline58 {        display: inline-block;   width:160px;  border-bottom: 1px solid #D0D0D0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline58-2017 {        display: inline-block;   width:160px;  border-bottom: 1px solid black; white-space: nowrap;  font-weight: bold;   }
            .standardunderline34 {        display: inline-block;   width:150px;  border-bottom: 1.8px solid #E0E0E0; white-space: nowrap;  font-weight: bold;   }
            .standardunderline34-2017 {        display: inline-block;   width:150px;  border-bottom: 1px solid black; white-space: nowrap;  font-weight: bold;   }
            .standardunderline34-2017notbold {        display: inline-block;   width:150px;  border-bottom: 1px solid black; white-space: nowrap;    }
            .standardunderline38notbold {        display: inline-block;   width:120px;  border-bottom: 1px solid black; white-space: nowrap;    }
            .standardunderlinehalf {        display: inline-block;   width:100px;   border-bottom: 1.8px solid #E0E0E0; white-space: nowrap; font-weight: bold;    }
            .standardulhalf2017 {        display: inline-block;   width:100px;   border-bottom: 1px solid black; white-space: nowrap; font-weight: bold;    }
            .standardunderlinehalfnotbold {        display: inline-block;   width:100px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap;   }
            .standardunderlinehalfnotbold-2017 {        display: inline-block;   width:100px;   border-bottom: 1px solid black; white-space: nowrap;   }
            .standardunderline38 {        display: inline-block;   width:80px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap; font-weight: bold;    }
            .standardunderline38-2017 {        display: inline-block;   width:80px;   border-bottom: 1px solid black; white-space: nowrap; font-weight: bold;    }
            .standardunderlinedate2023 {        display: inline-block;   width:100px;   border-bottom: 1.8px solid #D0D0D0; white-space: nowrap; font-weight: bold;    }
            .standardunderlinedate {        display: inline-block;   width:70px;   border-bottom: 1px solid #D0D0D0; white-space: nowrap; font-weight: bold;    }
            .standardunderlinedate2017 {        display: inline-block;   width:70px;   border-bottom: 1px solid black; white-space: nowrap; font-weight: bold;    }
            .standardunderlinedatenotbold {        display: inline-block;   width:75px;   border-bottom: 1.8px solid #E0E0E0; white-space: nowrap;     }
            .standardunderlinedatenotbold-2017 {        display: inline-block;   width:75px;   border-bottom: 1px solid black; white-space: nowrap;     }
            .standardunderlinedatenotboldforseasig {        display: inline-block;   width:73px;   border-bottom: 1.8px solid; white-space: nowrap;     } // #D0D0D0
            .standardunderlinenotboldforseasig {        display: inline-block;   width:250px;   border-bottom: 1.8px solid #D0D0D0; white-space: nowrap;     } // #D0D0D0
            .standardemptyverylong {        display: inline-block;   width:400px;    white-space: nowrap; font-weight: bold;    }
            .standardemptyverylongnotbold {        display: inline-block;   width:400px;    white-space: nowrap; font-weight: regular;    }
            .standardempty365 {        display: inline-block;   width:365px;    white-space: nowrap; font-weight: bold;    }
            .standardempty365notbold {        display: inline-block;   width:365px; bottom-border:1.8px;    white-space: nowrap;     }
            .standardempty350 {        display: inline-block;   width:350px;    white-space: nowrap; font-weight: bold;    }
            .standardempty350notbold {        display: inline-block;   width:350px;    white-space: nowrap;     }
            .standardempty340 {        display: inline-block;   width:340px;    white-space: nowrap; font-weight: bold;    }
            .standardempty340notbold {        display: inline-block;   width:340px;    white-space: nowrap; font-weight: regular;    }
            .standardempty320 {        display: inline-block;   width:320px;    white-space: nowrap; font-weight: bold;    }
            .standardempty320notbold {        display: inline-block;   width:320px;    white-space: nowrap; font-weight: regular;    }
            .standardempty320notboldforseasig {        display: inline-block;   width:316px;  border-bottom:red;  white-space: nowrap; font-weight: regular;    }
            .standardemptylonger {        display: inline-block;   width:380px;    white-space: nowrap; font-weight: bold;    }
            .standardemptylong {        display: inline-block;   width:300px;    white-space: nowrap; font-weight: bold;    }
            .standardempty365notbold {        display: inline-block;   width:365px;   border-bottom: 1.8px; white-space: nowrap;    }
            .standardemptylongnotbold {        display: inline-block;   width:300px;    white-space: nowrap;     }
            .standardempty275 {        display: inline-block;   width:275px;    white-space: nowrap; font-weight: bold;    }
            .standardempty275notbold {        display: inline-block;   width:275px;    white-space: nowrap;     }
            .standardempty250 {        display: inline-block;   width:250px;    white-space: nowrap; font-weight: bold;    }
            .standardempty250notbold {        display: inline-block;   width:250px;    white-space: nowrap;   }
            .standardempty {        display: inline-block;   width:200px;    white-space: nowrap;     }
            .standardempty34 {        display: inline-block;   width:150px;    white-space: nowrap;     }
            .standardempty34notbold {        display: inline-block;   width:150px;    white-space: nowrap;     }
            .standardempty38notbold {        display: inline-block;   width:120px;    white-space: nowrap;     }
            .standardempty188notbold {        display: inline-block;   width:188px;    white-space: nowrap;     }
            .standardemptyhalf {        display: inline-block;   width:100px;   white-space: nowrap;     }
            .standardemptydate {        display: inline-block;   width:70px;   white-space: nowrap;     }

            .standardunderlinetextarea {display: inline-block; width: 200px; height: 4em; border-bottom: 1px solid; overflow: hidden; white-space: nowrap; vertical-align: top; }
            .standardunderlinetextarea2 {display: inline-block; width: 200px; height: 2em; border-bottom: 1px solid; overflow: hidden; white-space: nowrap; vertical-align: top; }
            .textarea-wrapper { position: relative; width: 100%; height: 75px; line-height:12px; margin-left:0px; margin-top:-20px; }
            .textarea-2comments { position: relative; width: 100%; height:60px; line-height:12px; }
            .textarea-wrapper2023 { position: relative; width: 100%; height: 90px; line-height:11px; }

            .textarea-wrapper2 { position: relative; width: 100%; height: 60px; line-height:8px; }
            .textarea-wrapper3 { position: relative; height: 110px; line-height:10px; }
            .textarea-wrapperThree2023 { position: relative;  line-height:8px; background-color:transparent; }
            textarea { width: 100%; height: 100%; padding: 10px 0 0 5px; box-sizing: border-box;  background-color: transparent; resize: none; color: #000; } // border: 1px solid #ccc;
            .custom-textarea { width: 500px; padding: 0 5px;  background-color: white; color: #000; position:absolute; font-size: 9px;  height: 100%; overflow: hidden; white-space: pre-wrap; pointer-events: auto; }
            .custom-textarea2023 { z-index:100;width: 700px; padding: 0 5px;  background-color: transparent; color: #000; position:absolute; line-height:16px; font-size: 14px;  height: 95%; overflow: hidden; white-space: pre-wrap; pointer-events: auto; }
            .custom-textareaTwo2023 { z-index:100;width: 680px; padding: 0 5px;  background-color: transparent; color: #000; position:absolute; margin-top:-1.9em;line-height:14px; font-size: 14px;  height: 95%; overflow: hidden; white-space: pre-wrap; pointer-events: auto; }
            .custom-textarea3-2017 { width:95%; color: #000;font-size: 10px;  height: 1em;  }
            .custom-textarea3 { width: 95%; padding: 0 5px;  background-color: white; color: #000; position:absolute; font-size: 6px;  height: 90%; overflow: hidden; white-space: pre-wrap; pointer-events: auto; }
            .custom-textareaThree2023 { z-index:100;width: 700px; padding: 0 5px;   #000000ff;  line-height:1.7em; font-size: 14px;  pointer-events: auto; };
            .custom-textarea4 { width: 95%; padding: 0 5px;  background-color: white; color: #000; position:absolute; font-size: 6px;  height: 80%; overflow: hidden; white-space: pre-wrap; pointer-events: auto; }
            .custom-textareaFour2023 {  z-index:100;width: 690px; padding: 0 5px;  background-color: transparent; color: #000; position:absolute; line-height:16px; font-size: 14px;  height: 100%; overflow: hidden; white-space: pre-wrap; pointer-events: auto; }
            .custom-textarea5 { width: 95%; padding: 0 5px;  background-color: white; color: #000; position:absolute; font-size: 9px;  height: 100%; overflow: hidden; white-space: pre-wrap; pointer-events: auto; }
            .justified-text { text-align: justify; line-height: 1.8; text-indent: 0 !important;}
            .text-box { border: 0px solid black; padding: 0px; width: fit-content;text-align:justify;}
            .text-box-2017 { border: 0px solid black; padding: 0px; width: fit-content;text-align:justify;word-wrap:break-word;}
            .underlinefirstrow {      position: absolute; margin-top:15px; height: 1px; z-index:101; background-color: #E0E0E0; left: 5px; width:635px; padding-bottom:0px;  }
            .underlinefirstrow2 {      position: absolute; margin-top:10px; height: 1px; z-index:101; background-color: #E0E0E0; left: 5px; width:650px; padding-bottom:0px;  }
            .underlinefirstrow3 {      position: absolute; margin-top:18px; height: 1px; z-index:101; background-color: #E0E0E0; left: 5px; width:650px; padding-bottom:0px;  }
            .underlinefirstrow4 {      position: absolute; margin-top:16px; height: 1px; z-index:101; background-color: #E0E0E0; left: 5px; width:650px; padding-bottom:0px;  }            
            .underlinefirstrow2023 {      position: absolute; margin-top:15px; height: 1px; z-index:101; bottom-border: 1.8px solid #E0E0E0; background-color: #E0E0E0; left: 5px; width:735px; padding-bottom:0px;  }
            .underlinefirstrowTwo2023 {      position: absolute; margin-top:10px; height: 1px; z-index:101; background-color: #E0E0E0; left: 5px; width:750px; padding-bottom:0px;  }
            .underlinefirstrowThree2023 {      position: absolute; margin-top:18px; height: 1px; z-index:101; background-color: #E0E0E0; left: 5px; width:750px; padding-bottom:0px;  }
            .underlinefirstrowFour2023 {      position: absolute; margin-top:29px; height: 1px; z-index:101; background-color:  #E0E0E0; left: 5px; width:735px; padding-bottom:0px;  } // #E0E0E0

            .first-row { line-height: 0px; background-color:blue;z-index:100; }
            .textarea-wrapper2 { position: relative; width: 100%; margin-top:5px; height: 53px; }

            .textarea-wrapper4 { position: relative; width: 100%; margin-top:0px; height: 93px;}
            textarea2 { width: 100%; height: 100%; padding: 0px 0 0 5px; box-sizing: border-box;  background-color: transparent; resize: none; color: #000; } // border: 1px solid #ccc;
            .custom-textarea2 { width: 95%; height: calc(4 * 1.5em); padding: 0 5px;  background-color: white; color: #000; position:absolute; font-size: 9px; line-height: 1.6; height: 100%; overflow: hidden; resize:none; white-space: pre-wrap; pointer-events: auto; }
            .arrow-right { position: relative; display:inline-block; width: 30px; height:5px;top:-2px; background-color: black; }
            .arrow-right::after { content: ''; position: absolute; top: 53%; right: -10px;transform: translateY(-49%); width: 0; height: 0; border-top: 5px solid transparent; border-bottom: 5px solid transparent; border-left: 10px solid black; }        
            .borderboxfloat { border:1px solid #000; padding: 5px;float:right;}
            .fixedContainer {  position: fixed;  top: 0; left: 0;right: 0; z-index: 1000; }

           `;
        //      //@media print { .small-text { font-size:10px;} .smaller-text {font-size:8px;}  .p-screen-vs-print {line-height:12px;} .p-screen-vs-print2 {line-height:1px;} .textBoxAdjust { margin-top:0px;line-height:1.4;}}
        // the rest here are custom separate from the css in apiexports
        retStr += `
            @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
     </style>`;
        return retStr;
        /* 
                       section {
      padding: 40px 20px;
      margin-bottom: 60px;
      border-bottom: 2px solid red;
    }
            */
    }

    getSearchParmCoeExport() {
        const searchParmString = localStorage.getItem('searchParm');
        if (searchParmString) {
            return JSON.parse(searchParmString);
        }
        return null;
    }

    fnScriptSpinnerUI(): string {
        let retStr: string = `
                    <div id="spinner" style="display:none; position:fixed; top:20%; z-index:3000;left:50%; transform:translate(-50%, -50%);">                       
                        <i class="fa fa-circle-notch fa-spin fa-3x fa-fw" style="font-size:72px;solid;color:rgba(128, 128, 0, 0.3);"></i>       
                     <div style='display:none; position:fixed; top:50%; left:50%; transform:translate(-50%, -50%); width:48px; height:48px; border:5px solid rgba(0, 0, 0, 0.1); border-top:5px solid olive; border-radius:50%; animation:spin 1s linear infinite;'></div>
                    
                   <!-- <span *ngIf="loading" class="progress-spinner-container">
                            <mat-progress-spinner [color]="progressColor" mode="indeterminate">
                            </mat-progress-spinner>
                        </span> -->`;

        return retStr;
    }

    fnScriptCoeExport(): string {
        let retStr = `
        <script>
            progressColor: ThemePalette = 'accent';

            window.addEventListener('message', function(event) {
                    if (event.data === 'downloadComplete') {
                        hideSpinner();
                    }
            }, false);

            function showSpinner() {
                const spinner = document.getElementById('spinner');
                if (spinner) {
                    spinner.style.display = 'block';
                }
                setTimeout(hideSpinner, 19000);
            }

            function hideSpinner() {
                const spinner = document.getElementById('spinner');
                if (spinner) {
                    spinner.style.display = 'none';
                }
            }   

            function handleDownload() {
                showSpinner();
                window.opener.postMessage('downloadPdf', '*');
            }

            function debounce(func, wait) {
                let timeout;
                return function() {
                    if (timeout) return; // Prevent additional clicks 
                    func.apply(this, arguments);
                    timeout = setTimeout(() => { timeout = null; }, wait);
                };
            }
            
            const debounceDownload = debounce(handleDownload, 1000);
            </script>
        `;
        return retStr;
    }

    fnScriptCoeExport2(): string {
        let retStr = `
        <script>
            progressColor: ThemePalette = 'accent';

             window.addEventListener('message', function(event) {
                    if (event.data === 'downloadComplete') {
                        hideSpinner();
                    }
            }, false);
        </script>
        `;

        return retStr;
    }

    fnScriptLinks(): string {
        let retStr: string = `
                  <link rel='stylesheet' href='https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css'>
                  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
                    `;
        return retStr;
    }

    public async makeRequestCOEBulkExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any,
        bOpen: boolean
    ) {
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        const httpOptions2 = {
            headers: { 'Content-Type': 'application/json' },
            responseType: 'text' as const
        };
        console.log(httpOptions);

        // Kick off the main http post to get the export data
        if (!bOpen) {
            const response = await this.http
                .post(url, body, httpOptions)
                .toPromise()
                .then(
                    (response: any) => {
                        console.log('positive response recieved');
                        //console.log(response)
                        clearInterval(timer);

                        const blob = new Blob([response], { type: blobType });

                        console.log('blobType:');
                        console.log(blobType);

                        if (blobType == 'application/xlsx') {
                            const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                            const url = URL.createObjectURL(renamedFile);

                            const a = document.createElement('a');
                            a.href = url;
                            fileNameLabel = fileNameLabel.trimLeft();
                            fileName = fileName.trimLeft();
                            if (bAddTimeStamp) {
                                fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                            }

                            console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                            a.download = fileName + '.xlsx';
                            a.click();
                            if (a && a.parentNode === document.body) {
                                document.body.removeChild(a);
                            }

                            URL.revokeObjectURL(url);
                        } else {
                            const url = URL.createObjectURL(blob);

                            const a = document.createElement('a');
                            a.href = url;
                            fileName = fileName.trimLeft();

                            if (bAddTimeStamp) {
                                fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                            }
                            console.log('will overwrite current and name the file ' + fileName + '.pdf');

                            a.download = fileName + '.pdf';
                            a.click();
                            if (a && a.parentNode === document.body) {
                                document.body.removeChild(a);
                            }

                            URL.revokeObjectURL(url);
                        }
                    },
                    (error) => {
                        console.log('Error in makeRequestCOEBulkExportData of api-exports.service:' + JSON.stringify(error));
                        throw error;
                    }
                );
        } else {
            let response2: ArrayBuffer = await firstValueFrom(this.http.post<ArrayBuffer>(url, body, httpOptions));

            const decoder = new TextDecoder('utf-8');
            let responseString = decoder.decode(response2);
            console.log('the response html is:');
            console.log(responseString);

            let startHeader: number = responseString.indexOf('<body>') + 6;
            let parseFormYear = responseString.indexOf('page-break-after', 40) - 149; //143; //89;//151; //88; // 65-23; // for 2017 this is: -69
            let formYear: string = responseString.substring(parseFormYear, parseFormYear + 4).trim();
            let startFirstPage: number = responseString.indexOf('MIGRANT EDUCATION PROGRAM') + 75;
            let gridContainerStart: number = 0;
            let offset2024: number = 0;
            console.log('formYear:', formYear);
            let startSecondPage = 0;
            let startSecondPage2024: number = responseString.indexOf('page-break-after', 40) + 52; //34;
            let startSecondPage2017: number = responseString.indexOf('CERTIFICATE OF ELIGIBILITY - Part 2', 40) - 98; //82;
            if (formYear == '2024') {
                console.log('This is a 2024 form');
                //offset2024 = 30-73; //-83; //44; //-83;//-7; //-83; // -89
                //offset2024 = 31;
                startSecondPage = startSecondPage2024 + offset2024;
            } else {
                formYear = '2017';
                startSecondPage = startSecondPage2017;
            }

            let headerOnly: string = responseString.substring(startHeader, startFirstPage);
            let firstPageOnly: string = '';
            if (formYear == '2017') {
                gridContainerStart = responseString.indexOf('gridcontainerstart') - 9;
                firstPageOnly = responseString.substring(gridContainerStart, startSecondPage);
            } else {
                gridContainerStart = responseString.indexOf('gridcontainerstart', 0) - 9;
                firstPageOnly = responseString.substring(gridContainerStart, startSecondPage);
            }
            let firstPageHeaderOnly: string = responseString.substring(startHeader, gridContainerStart); // 273   // - 190      // 356

            let secondPageOnly: string = responseString.substring(startSecondPage);
            console.log('header:');
            console.log(headerOnly);
            console.log('formyear:', formYear);
            //console.log("firstpageheaderonly:")
            //console.log(startFirstPageHeader)
            //console.log(startFirstPage)
            //console.log(firstPageHeaderOnly);
            console.log('first page:');
            console.log(firstPageOnly);
            console.log('second page');
            console.log(secondPageOnly);
            const newTab = window.open('about:blank', '_blank');
            let htmlString: string = '';
            if (newTab) {
                newTab.document.open();

                if (formYear == '2024') {
                    htmlString += `<!DOCTYPE html>
                    <html>
                        <head>
                        <title>Print Viewer</title>`;
                    htmlString += this.fnScriptLinks();

                    htmlString += this.fnScriptCoeExport2();
                    htmlString += this.fnWriteCssForPdfExport();
                    htmlString += this.fnScriptCoeExport();

                    htmlString += `</head>
                        <body>
                        <div id="spinner" style="display:none; position:fixed; top:20%; z-index:3000;left:50%; transform:translate(-50%, -50%);">                       
                            <i class="fa fa-circle-notch fa-spin fa-3x fa-fw" style="font-size:72px;solid;color:rgba(128, 128, 0, 0.3);"></i>       
                            <div style='display:none; position:fixed; top:50%; left:50%; transform:translate(-50%, -50%); width:48px; height:48px; border:5px solid rgba(0, 0, 0, 0.1); border-top:5px solid olive; border-radius:50%; animation:spin 1s linear infinite;'>
                            </div>
                        </div>
                            <div style="position:relative;width:1160px; margin:0 auto; ">        
                            <div class="controls">
                                <div style="padding: 10px;text-align: center;">  
                                <button onclick="showSpinner();debounceDownload();">Download PDF</button>
                                </div>
                            </div>`;

                    htmlString += `
                                <div id="content2" style="position:absolute;  top:65px; margin-left: -180px; text-align:left;color:black !important;width:1580px;">
                                ${firstPageHeaderOnly}  
                                </div>   `;

                    htmlString += ` <div id="content3" class="grid-container2023all" style="position:absolute; top:30px; transform:translateX(-50%);left:35%;height:975x;width: 1090px;text-align:left; color:black !important;">                                        
                                ${firstPageOnly}                                
                                `; // Note: there should be NO <body> in firstPageOnly and no closing div, because it is included at start of page 2 after the page break

                    htmlString += `  </div></div></div>    `;
                    htmlString += `

                                 <div  id="content4" style="position:absolute;transform:translateX(-50%);left: 52%; margin-top:1040px;width:1580px;">
                                    ${secondPageOnly}
                                </div>
                                <div>
                                <p style='line-height:20px;'>&nbsp;.</p>
                                </div>`;
                    htmlString += `  
                        </div>
                        </body>
                    </html>
                    `;

                    newTab.document.write(htmlString);
                } else {
                    console.log('now printing 2017 form');

                    htmlString += `
                    <!DOCTYPE html>
                    <html>
                        <head>
                        <title>Print Viewer</title>
                    `;
                    htmlString += this.fnScriptLinks();

                    htmlString += this.fnScriptCoeExport2();
                    htmlString += this.fnWriteCssForPdfExport();
                    htmlString += this.fnScriptCoeExport();

                    htmlString += `   </head>
                        <body>
                        <div id="spinner" style="display:none; position:fixed; top:20%; z-index:3000;left:50%; transform:translate(-50%, -50%);">                       
                           <i class="fa fa-circle-notch fa-spin fa-3x fa-fw" style="font-size:72px;solid;color:rgba(128, 128, 0, 0.3);"></i>       
                        <div style='display:none; position:fixed; top:50%; left:50%; transform:translate(-50%, -50%); width:48px; height:48px; border:5px solid rgba(0, 0, 0, 0.1); border-top:5px solid olive; border-radius:50%; animation:spin 1s linear infinite;'></div>
                        </div>
                        `;
                    htmlString += `
						   <div style="position:relative;width:1160px; margin:0 auto;color:black !important; ">
                                <div class="controls">
                                    <div style="padding: 10px;text-align: center;">  
                                    <button onclick="showSpinner();window.opener.postMessage('downloadPdf', '*');">Download PDF</button>
                                    </div>
                                </div>
                    `;
                    htmlString += `                             
                            <div id="content2" style="position:absolute;  top:65px; margin-left: 0px; text-align:left;color:black !important;width:1135px;">
                            ${firstPageHeaderOnly}
                            </div> `;

                    htmlString += `        
                            <div id="content3" class="grid-container2017" style="position:absolute; height:645px; top:10px; margin-left: 0px; text-align:left; color:black !important;width:1155px;"> 
                            ${firstPageOnly}
                            </div>
                           `; // Note: there should be NO <body> in firstPageOnly and no closing div, because it is included at start of page 2 after the page break
                    //1118px
                    htmlString += `                    
                            <div id="content4" style="position:absolute; margin-top:785px;margin-left:0px; width:1135px;"> 
                               ${secondPageOnly}
                              </div>     `;
                    htmlString += `    </div>
                        </body>
                    </html>
                    `;

                    newTab.document.write(htmlString);
                }
                newTab.document.close();
            } else {
                console.error('Could not open new tab');
            }
        }
    }

    public async makeRequestServiceStudentData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        console.log(httpOptions);

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);
                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);
                    const parsedBody = JSON.parse(body);
                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        console.log('at this point the filename is:');
                        console.log(fileName);
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestCOEExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestChildReportExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;
                        console.log('body here is:');
                        console.log(parsedBody);
                        console.log(parsedBody.chosenPerformancePeriod);
                        console.log('reportType:');
                        console.log(parsedBody['reportType']);

                        if (parsedBody['reportType'] == 1) {
                            fileName = 'InstantEligibililtyCountsReportCatIandCatII_';
                            fileName += parsedBody['chosenPerformancePeriod'];
                            fileName += '_Region_';
                            fileName += parsedBody['countsDetail'][0].Region;
                        } else {
                            fileName += '_' + parsedBody['chosenPerformancePeriod'];
                        }

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        if (body['reportType'] == 1) {
                            fileName = 'InstantEligibililtyCountsReportCatIandCatII_';
                            fileName += body['performancePeriod'];
                            fileName += '_Region_';
                            fileName += body['countsDetail'][0].Region;
                        } else {
                            fileName += '_' + body['chosenPerformancePeriod'];
                        }

                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestChildReportExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestEnrollmentsExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any,
        csvOption: boolean
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        let districtsPart: string = '';
                        districtsPart = parsedBody['chosenSchoolDistricts']?.DISTRICTS;
                        districtsPart = districtsPart.replace(/ /g, '_');
                        fileName += '_' + districtsPart + '_' + parsedBody['chosenPerformancePeriod'];

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else if ((blobType = 'csv')) {
                        console.log('exporting as csv');
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        let districtsPart: string = '';
                        districtsPart = parsedBody['chosenSchoolDistricts']?.DISTRICTS;
                        districtsPart = districtsPart.replace(/ /g, '_');
                        fileName += '_' + districtsPart + '_' + parsedBody['chosenPerformancePeriod'];

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.csv');
                        a.download = fileName + '.csv';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        console.log('exporting as pdf');
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        let districtsPart: string = '';
                        districtsPart = parsedBody['chosenSchoolDistricts']?.DISTRICTS;
                        districtsPart = districtsPart.replace(/ /g, '_');
                        fileName += '_' + districtsPart + '_' + parsedBody['chosenPerformancePeriod'];

                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestEnrollmentsExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestServiceReportExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any,
        strRegion?: string
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();

                        if (bAddTimeStamp) {
                            let arrRegions: Region[] = parsedBody['chosenRegions'] as Region[];
                            let regionsCheck: number = arrRegions.length;
                            let ind: number = parsedBody['schoolsInd'] as number;

                            if (parsedBody['reportType'] == 0) {
                                if (regionsCheck > 1 && ind != 1) {
                                    fileName =
                                        fileName + '_' + parsedBody['chosenPerformancePeriod'] + '_SingleSheet_' + this.utilityService.fnFormatDateTime();
                                } else {
                                    if (arrRegions[ind]?.CODE == null) {
                                        ind = 0;
                                    }

                                    const safeIndex = ind != null && ind >= 0 && ind < arrRegions.length ? ind : 0;
                                    strRegion = arrRegions[safeIndex]?.CODE ?? '';
                                    fileName =
                                        fileName +
                                        '_Region_' +
                                        strRegion +
                                        '_' +
                                        parsedBody['chosenPerformancePeriod'] +
                                        '_SingleSheet_' +
                                        this.utilityService.fnFormatDateTime();
                                }
                            } else {
                                if (regionsCheck > 1 && ind != 1) {
                                    fileName =
                                        fileName + '_' + parsedBody['chosenPerformancePeriod'] + '_SeparatedSheets_' + this.utilityService.fnFormatDateTime();
                                } else {
                                    if (arrRegions[ind]?.CODE == null) {
                                        ind = 0;
                                    }

                                    const safeIndex2 = ind != null && ind >= 0 && ind < arrRegions.length ? ind : 0;

                                    strRegion = arrRegions[safeIndex2].CODE ?? '';
                                    fileName =
                                        fileName +
                                        '_Region_' +
                                        strRegion +
                                        '_' +
                                        parsedBody['chosenPerformancePeriod'] +
                                        '_SeparatedSheets_' +
                                        this.utilityService.fnFormatDateTime();
                                }
                            }
                        }
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');

                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else // FOR PDF
                    {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestServiceReportExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestAcademicRiskExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any,
        isChosenSchool: boolean,
        schoolName?: string
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        if (bAddTimeStamp) {
                            if (isChosenSchool) {
                                fileName = fileName + '_' + fileNameLabel + '_' + this.utilityService.fnFormatDateTime();
                            } else {
                                fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                            }
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestAcademicRiskExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestInaPdfExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        isChosenSchool: boolean,
        schoolName?: string,
        bOpen?: boolean
    ): Promise<string> {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response: any = await this.http.post(url, body, httpOptions).toPromise();

        try {
            console.log('positive response recieved');
            //console.log(response)
            clearInterval(timer);

            const blob = new Blob([response], { type: blobType });

            console.log('blobType:');
            console.log(blobType);

            if (blobType == 'application/xlsx') {
                /*    const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                    const url = URL.createObjectURL(renamedFile);

                    const a = document.createElement('a');
                    a.href = url;

                    //fileName += ("_" + parsedBody["chosenPerformancePeriod"]);

                    console.log("at this point the filename is:")
                    console.log(fileName)
                    if (bAddTimeStamp) {
                        if (isChosenSchool) {
                            fileName = fileName + "_" + fileNameLabel + "_" + this.utilityService.fnFormatDateTime();
                        }
                        else {
                            fileName = fileName + "_" + this.utilityService.fnFormatDateTime();
                        }
                    }

                    fileNameLabel = fileNameLabel.trimLeft();
                    fileName = fileName.trimLeft();
                    console.log("will overwrite current and name the file " + fileName + ".xlsx");
                    a.download = fileName + ".xlsx";
                    a.click();
                    if (a && a.parentNode === document.body) {
                        document.body.removeChild(a);
                    }

                    URL.revokeObjectURL(url);*/
            } else {
                const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                const url = URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = url;

                if (bAddTimeStamp) {
                    fileName = fileName + '_' + fileNameLabel + '_' + this.utilityService.fnFormatDateTime();
                    console.log('will overwrite current and name the file ' + fileName + '.pdf');
                }
                fileName = fileName.trimLeft();
                a.download = fileName + '.pdf';
                a.click();
                if (a && a.parentNode === document.body) {
                    document.body.removeChild(a);
                }
                URL.revokeObjectURL(url);
            }
            return Promise.resolve(fileName);
        } catch (error) {
            console.log('Error in makeRequestInaPdfExportData of api-exports.service:' + JSON.stringify(error));
            throw error;
        }
    }

    public async makeRequestINAViewData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestINAViewData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestPFSData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenSchoolDistricts'].DISTRICTS + '_' + parsedBody['chosenPerformancePeriod'];

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenSchoolDistricts'].DISTRICTS + '_' + parsedBody['chosenPerformancePeriod'];

                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestPFSData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestGraduationData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName += '_' + parsedBody['chosenPerformancePeriod'];

                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestGraduationData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestInaData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestInaData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestCommunicationEventExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        const parsedBody = JSON.parse(body);
        // run a reset idle timer to prevent auto-logout while a long report is generating
        /*const interval = 1200000;
        const timer = setInterval(()=>{
            console.log("now calling resetTimer from subject api-exports.service")
            this.timeoutResetSubject.next(undefined);
        }, interval);	
    */
        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        console.log(httpOptions);

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    ///clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }
                        fileName = fileName.trimLeft();
                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestCommunicationEventExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestCOEReviewLogExportData(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);
        //
        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(url);
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestCOEReviewLogExportData of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }
    public async makeRequestCOESearch(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(url);
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        /*   if (bAddTimeStamp)
                       {
                           fileName = fileName + "_" + this.utilityService.fnFormatDateTime();   
                       }*/

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestCOESearch of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestDtMonEnrollments(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        periodYears: string,
        bAddTimeStamp: boolean,
        colWidths: any
    ) {
        if (fileName.includes(periodYears)) {
            if (fileNameLabel.includes(periodYears)) {
                fileName = fileName.replace('(' + periodYears + ')', '');
            }
        }

        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    console.log('positive response recieved');
                    console.log(response);
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    console.log('Error in makeRequestDtMonEnrollments of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestDtMonEnrollmtAndService(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        periodYears: string,
        bAddTimeStamp: boolean
    ) {
        if (fileName.includes(periodYears)) {
            if (fileNameLabel.includes(periodYears)) {
                fileName = fileName.replace('(' + periodYears + ')', '');
            }
        }

        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };
        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('positive response recieved');
                    console.log(response);
                    console.log('blobType:');
                    console.log(blobType);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;

                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    clearInterval(timer);

                    console.log('Error in makeRequestDtMonEnrollments of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestDtMonSimplifiedEnrollmt(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        periodYears: string,
        bAddTimeStamp: boolean
    ) {
        if (fileName.includes(periodYears)) {
            if (fileNameLabel.includes(periodYears)) {
                fileName = fileName.replace('(' + periodYears + ')', '');
            }
        }

        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };
        // Kick off the main http post to get the export data
        const response = await this.http
            .post(url, body, httpOptions)
            .toPromise()
            .then(
                (response: any) => {
                    clearInterval(timer);

                    const blob = new Blob([response], { type: blobType });

                    console.log('positive response recieved');
                    console.log(response);

                    if (blobType == 'application/xlsx') {
                        const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                        const url = URL.createObjectURL(renamedFile);

                        const a = document.createElement('a');
                        a.href = url;
                        fileNameLabel = fileNameLabel.trimLeft();
                        fileName = fileName.trimLeft();
                        if (bAddTimeStamp) {
                            fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                        }

                        console.log('will overwrite current and name the file ' + fileName + '.xlsx');
                        a.download = fileName + '.xlsx';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    } else {
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        fileName = fileName.trimLeft();
                        console.log('will overwrite current and name the file ' + fileName + '.pdf');

                        a.download = fileName + '.pdf';
                        a.click();
                        if (a && a.parentNode === document.body) {
                            document.body.removeChild(a);
                        }

                        URL.revokeObjectURL(url);
                    }
                },
                (error) => {
                    clearInterval(timer);

                    console.log('Error in makeRequestDtMonSimplifiedEnrollmt of api-exports.service:' + JSON.stringify(error));
                    throw error;
                }
            );
    }

    public async makeRequestDtMon(
        url: string,
        method: string,
        headers: any,
        body: any,
        blobType: string,
        fileName: string,
        fileNameLabel: string,
        template_name: string,
        periodYears: string,
        bAddTimeStamp: boolean
    ) {
        // run a reset idle timer to prevent auto-logout while a long report is generating
        const interval = 60000;
        const timer = setInterval(() => {
            console.log('now calling resetTimer from subject api-exports.service');
            this.timeoutResetSubject.next(undefined);
        }, interval);

        headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');
        console.log('now kicking off main post with body:');
        console.log(url);
        console.log(body);
        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };

        // Kick off the main http post to get the export data
        try {
            const response = await this.http
                .post(url, body, httpOptions)
                .toPromise()
                .then(
                    (response: any) => {
                        clearInterval(timer);

                        const blob = new Blob([response], { type: blobType });

                        if (blobType == 'application/xlsx') {
                            const renamedFile = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

                            const url = URL.createObjectURL(renamedFile);

                            const a = document.createElement('a');
                            a.href = url;
                            fileNameLabel = fileNameLabel.trimLeft();
                            fileName = fileName.trimLeft();
                            if (bAddTimeStamp) {
                                fileName = fileName + '_' + this.utilityService.fnFormatDateTime();
                            }
                            console.log('will overwrite current and name the file ' + fileName + ':' + '.xlsx');
                            a.download = fileName + '.xlsx';
                            a.click();
                            if (a && a.parentNode === document.body) {
                                document.body.removeChild(a);
                            }

                            URL.revokeObjectURL(url);
                        } else {
                            const url = URL.createObjectURL(blob);

                            const a = document.createElement('a');
                            a.href = url;
                            fileNameLabel = fileNameLabel.trimLeft();
                            fileName = fileName.trimLeft();
                            console.log('will overwrite current and name the file ' + fileNameLabel + ':' + fileName + '.pdf');
                            a.download = fileNameLabel + fileName + '.pdf';
                            a.click();
                            if (a && a.parentNode === document.body) {
                                document.body.removeChild(a);
                            }

                            URL.revokeObjectURL(url);
                        }
                    },
                    (error) => {
                        clearInterval(timer);

                        console.log('Error in makeRequestDtMon of api-exports.service:' + JSON.stringify(error));
                        throw error;
                    }
                );
        } catch (Err) {
            console.log('Caught error in api-exports:' + Err);
        }
    }

    public async getDtMonReportPDFViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            schoolYear: data['strYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['strTemplateName'],
            studentIds: JSON.parse(data['studentIds']),
            file_name: data['strFileName'],
            fileNameLabel: data['fileNameLabel'],
            bTable: data['bTable'],
            grade: data['grade'],
            age: data['age'],
            columns: data['columns'],
            columnWidths: data['columnWidths'],
            columnHeaders: data['columnHeaders'],
            isAdvancedTable: data['isAdvancedTable'],
            tableHtml: data['tableHtml']
        };

        const url = this.urlProvider.getUrl4DtMonGradeAgePDF();

        var headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=utf-8').set('Content-Type', 'application/json').set('responseType', 'blob');

        const httpOptions = {
            responseType: 'arraybuffer' as 'json',
            headers: headers
        };
        try {
            await this.makeRequestDtMonEnrollments(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/pdf',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.schoolYear,
                true,
                params.columnWidths
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getCoeSearchPDFViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const authTkn = authObject.headers?.lazyUpdate[2]?.value ?? '';

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCity: data['chosenCity'],
            chosenRecruiterName: data['chosenRecruiterName'],
            chosenIntervieweeName: data['chosenIntervieweeName'],
            chosenSignatureDateStart: data['chosenSignatureDateStart'],
            chosenSignatureDateEnd: data['chosenSignatureDateEnd'],
            chosenCoeStatus: data['chosenCoeStatus'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['strTemplateName'],
            file_name: data['strFileName'],
            coes: JSON.parse(data['coes']),
            coedetail: JSON.parse(data['coedetail']),
            fileNameLabel: data['file_name_label'],

            authObj1: JSON.stringify(authObject),
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrlCoeExportData();

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authObject.headers.lazyUpdate[2].value
        };
        try {
            await this.makeRequestCOESearch(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/pdf',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                true,
                params.columnWidths
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getDtMonEnrollmentsPDFViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            schoolYear: data['strYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            template_name: data['strTemplateName'],
            studentIds: JSON.parse(data['studentIds']),
            file_name: data['strFileName'],
            fileNameLabel: data['fileNameLabel'],
            grade: data['grade'],
            age: data['age'],
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrl4DtMonEnrollmentsPDF();

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authObject.headers.lazyUpdate[2].value
        };
        try {
            await this.makeRequestDtMonEnrollments(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/pdf',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.schoolYear,
                true,
                params.columnWidths
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getDtMonEnrollmtTypeAndServicePDFViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            schoolYear: data['strYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            studentIds: JSON.parse(data['studentIds']),
            file_name: data['strFileName'],
            fileNameLabel: data['fileNameLabel'],
            template_name: data['Template_Name'],
            grade: data['grade'],
            age: data['age'],
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrl4DtMonEnrollmtsAndService();

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authObject.headers.lazyUpdate[2].value
        };
        try {
            await this.makeRequestDtMonEnrollmtAndService(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/pdf',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.schoolYear,
                true
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getDtMonSimplifiedEnrollmtPDFViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();

        var params = {
            chosenRegions: data['chosenRegions'],
            chosenCounties: data['chosenCounties'],
            chosenSchools: data['chosenSchools'],
            chosenSchoolDistricts: data['chosenSchoolDistricts'],
            schoolYear: data['strYear'],
            export_type: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            studentIds: JSON.parse(data['studentIds']),
            file_name: data['strFileName'],
            fileNameLabel: data['fileNameLabel'],
            template_name: data['Template_Name'],
            grade: data['grade'],
            age: data['age'],
            columns: data['columns'],
            columnHeaders: data['columnHeaders'],
            columnWidths: data['columnWidths']
        };

        const url = this.urlProvider.getUrl4DtMonSimplifiedEnrollmt();

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authObject.headers.lazyUpdate[2].value
        };
        try {
            await this.makeRequestDtMonSimplifiedEnrollmt(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/pdf',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.schoolYear,
                true
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getSSDPGeneralPDFViaPost(data: any) {
        const authObject = this.urlProvider.baseGetOptions();

        var params = {
            region_category: data['strRegionCategory'],
            district_id: '',
            year: data['strYear'],
            msd_number: '',
            export_type: data['strExportType'],
            second_last_name: '',
            bCheckedStyling: data['bCheckedStyling'] ? data['bCheckedStyling'] : 'false',
            template_name: data['strTemplateName'],
            dob: '',
            sex: '',
            ssid: '',
            studentIds: JSON.parse(data['studentIds']),
            file_name: data['strFileName'],
            fileNameLabel: data['fileNameLabel']
        };

        const url = this.urlProvider.getUrlNoQueryParamViaPostSSDPGeneral(EndPoints.apiMsinExportsSSDPGeneral);

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authObject.headers.lazyUpdate[2].value
        };
        try {
            await this.makeRequest(
                url,
                'POST',
                headers,
                JSON.stringify(params),
                'application/pdf',
                params.file_name,
                params.fileNameLabel,
                params.template_name,
                params.year
            );
        } catch (Err) {
            throw Err;
        }
    }

    public async getSSDPReportPDFViaPost(data: any) {
        const url = this.urlProvider.getUrlNoQueryParamViaPost(EndPoints.apiMsinExportsPDF);
        const authObject = this.urlProvider.baseGetOptions();

        let params: ExportParams;
        params = {
            strRegionCategory: data['strRegionCategory'],
            strDistrictId: data['strDistrictId'],
            strYear: data['strYear'],
            strExportType: data['strExportType'],
            bCheckedStyling: data['bCheckedStyling'],
            strHtmlContent: 'test',
            strTemplateName: data['strTemplateName'],
            authObj1: JSON.stringify(authObject)
        };

        var headers = {
            'Access-Control-Allow-Origin': environment.headerHost,
            'Content-Type': 'application/json',
            Accept: '*/*',
            responseType: 'text' as 'json',
            Authorization: authObject.headers.lazyUpdate[2].value
        };

        const response = await this.http.post(url, params, (Headers = { ...this.urlProvider.baseGetOptions(), headers })).subscribe(
            (response: any) => {
                console.log('the response is:');
                console.log(response);

                this.openPdfInNewTab(response);
            },
            (error: any) => {
                console.log('had an error:', error);
            }
        );
    }

    openPdfInNewTab(response: string): void {
        console.log('in openPdfInNewTab()');
        const byteCharacters = atob(response); //.body; //atob(response);

        const byteNumbers = new Array(byteCharacters.length);
        console.log('len');
        console.log(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        // console.log(byteArray)
        const pdfData = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfData);
        window.location.replace(url);

        URL.revokeObjectURL(url);
    }

    public getSSDPAnnualReportPDF(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const url = this.urlProvider.getUrlNoQueryParamAnnualSSDP();

        let urlParam = '?';
        urlParam += 'chosenRegions=' + (data.chosenRegions ? data.chosenRegions : '');
        urlParam += '&strYear=' + (data.strYear ? data.strYear : '');
        urlParam += '&strTemplateName=' + (data.strTemplateName ? data.strTemplateName : '');
        urlParam += '&strExportType=PDF';
        urlParam += '&bCheckedStyling=' + data.bCheckedStyling;
        urlParam += '&authObj1=' + JSON.stringify(authObject);
        let newurl = url + '' + urlParam;

        window.location.replace(newurl);
    }

    public getSSDPGeneralPDF(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const url = this.urlProvider.getUrlNoQueryParamViaPostSSDPGeneral(EndPoints.apiMsinExportsSSDPGeneral);

        let urlParam = '?';
        urlParam += 'strRegionCategory=' + (data.strRegionCategory ? data.strRegionCategory : '');
        urlParam += '&strDistrictId=' + (data.strDistrictId ? data.strDistrictId : '');
        urlParam += '&strYear=' + (data.strYear ? data.strYear : '');
        urlParam += '&strTemplateName=' + (data.strTemplateName ? data.strTemplateName : '');
        urlParam += '&strExportType=PDF';
        urlParam += '&bCheckedStyling=' + data.bCheckedStyling;
        urlParam += '&authObj1=' + JSON.stringify(authObject);
        urlParam += '&studentIds=' + (data.studentIds ? data.studentIds : '');
        let newurl = url + '' + urlParam;

        window.location.replace(newurl);
    }

    public getSSDPReportPDF(data: any) {
        const authObject = this.urlProvider.baseGetOptions();
        const url = this.urlProvider.getUrlNoQueryParam();

        let urlParam = '?';
        urlParam += 'strRegionCategory=' + (data.strRegionCategory ? data.strRegionCategory : '');
        urlParam += '&strDistrictId=' + (data.strDistrictId ? data.strDistrictId : '');
        urlParam += '&strYear=' + (data.strYear ? data.strYear : '');
        urlParam += '&strTemplateName=' + (data.strTemplateName ? data.strTemplateName : '');
        urlParam += '&strExportType=PDF';
        urlParam += '&bCheckedStyling=' + data.bCheckedStyling;
        urlParam += '&authObj1=' + JSON.stringify(authObject);
        let newurl = url + '' + urlParam;

        window.location.replace(newurl);
    }
}
